// ─── Images on a request ─────────────────────────────────────────────────────
//
// Screenshots that were on the Slack ask, or on the thread before it. The card
// holds references only: squish-backend fetches the bytes from Slack each time
// (and only a file the card recorded, only if its bytes are really an image),
// so an image deleted in Slack goes from the card too.
//
// Same rendering rule as CaseAttachments: fetched with the staff headers and
// shown from a blob: URL in an <img>, so the browser decodes pixels and
// nothing else. Cached per image for the life of the tab, so reopening a card
// does not go back to Slack.

import React, { useEffect, useState } from 'react';
import { fetchRequestImage } from '../api';

function useImageUrl(requestId, fileId) {
  const [state, setState] = useState({ url: null, error: null });
  useEffect(() => {
    if (!requestId || !fileId) return undefined;
    let alive = true;
    setState({ url: null, error: null });
    fetchRequestImage(requestId, fileId)
      .then(({ url }) => alive && setState({ url, error: null }))
      .catch((e) => alive && setState({ url: null, error: e.message || 'Could not load the image' }));
    return () => { alive = false; };
  }, [requestId, fileId]);
  return state;
}

// One thumbnail. Clicking it opens the image large.
export function RequestImageThumb({ requestId, image, onOpen }) {
  const { url, error } = useImageUrl(requestId, image.id);
  return (
    <button type="button" className="req-image" onClick={() => onOpen(image)} title={`Open ${image.name}`} disabled={!url}>
      {url
        ? <img src={url} alt={image.name} />
        : <span className={`req-image-state${error ? ' is-error' : ''}`}>{error || 'Loading…'}</span>}
      <span className="req-image-name">{image.name}{image.from === 'thread' ? ' · from the thread' : ''}</span>
    </button>
  );
}

// The image, large. Escape closes it, and only it: the listener runs in the
// capture phase so the drawer behind it does not close as well.
export function RequestImageLightbox({ requestId, image, onClose }) {
  const { url, error } = useImageUrl(requestId, image.id);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); onClose(); } };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className="req-lightbox" role="dialog" aria-modal="true" aria-label={image.name} onClick={onClose}>
      <div className="req-lightbox-bar" onClick={e => e.stopPropagation()}>
        <span className="req-lightbox-name">{image.name}</span>
        <button type="button" className="req-lightbox-close" onClick={onClose} aria-label="Close image">✕</button>
      </div>
      {url
        ? <img src={url} alt={image.name} onClick={e => e.stopPropagation()} />
        : <div className={`req-lightbox-state${error ? ' is-error' : ''}`}>{error || 'Loading…'}</div>}
    </div>
  );
}
